import { AbstractProgram, AbstractSession } from "@kvix/shared";
import { Button, Placeholder, ReplayListItem, RichText, Text } from "@kvix/ui";
import {
  Box,
  Collapse,
  Container,
  Divider,
  Grid,
  makeStyles,
  Theme,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { formatDistanceToNow } from "date-fns";
import { sv } from "date-fns/locale";
import i18next from "i18next";
import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Redirect, useParams } from "react-router";
import { Link } from "react-router-dom";
import { KvixUserContext } from "../../../contexts/user";
import { useFetchProgram } from "../../../hooks/http";
import { useIsMobile } from "../../../hooks/isMobile";
import { Footer } from "../../Footer";
import { FavouriteSession } from "../../partials/GetFavourite";
import { InstructorCard } from "../../partials/Instructor/InstructorCard";
import { Page } from "../../partials/Page";
import { PremiumVideoPlaceholder } from "../../partials/PremiumVideoPlaceholder/PremiumVideoPlaceholder";
import { AccessLevel, useUserAccessLevel } from "../../PurchaseFlow";
import { ProgramComments } from "../Program/Comments";
import { ReplayContent } from "./Content";

const useStyles = makeStyles((theme) => ({
  displayMoreButton: {
    textTransform: "none",
    height: "36px",
    padding: "0 14px",
    marginTop: theme.spacing(2),
  },
  heartContainer: {
    "& .MuiIconButton-root": {
      padding: "5px 12px 12px 12px",
    },
    right: 0,
    marginLeft: "auto",
    marginRight: "0px",
  },
  containerPaddingBottom: {
    paddingBottom: "32px",
  },
}));

const ReplayPresentation: React.FC<{
  program: AbstractProgram;
  session: AbstractSession;
  theme: Theme;
}> = (props) => {
  const { t, ready } = useTranslation("replay");
  const { program, session, theme } = props;
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const classes = useStyles();

  if (!ready) {
    return null;
  }

  return (
    <Collapse in={!!program}>
      <Box py={4}>
        <Text variant="h2" gutterBottom>
          {program?.title}
        </Text>

        <div style={{ display: "flex" }}>
          <Text style={{ lineHeight: 1.5 }}>
            {t("with")}
            <Link to={`/instructor/${program?.instructor.id}`}>
              <span style={{ fontWeight: 500 }}>
                {t("instructor", {
                  firstName: program?.instructor.name.first,
                  lastName: program?.instructor.name.last,
                })}
              </span>
              <span style={{ display: isMobile ? "block" : "inline" }}>
                {t("wasLiveTimeAgo", {
                  time:
                    session &&
                    formatDistanceToNow(new Date(session.start), {
                      locale: i18next.language === "sv-SE" && sv,
                    }),
                })}
              </span>
            </Link>
          </Text>
          <span className={classes.heartContainer}>
            {<FavouriteSession session={{ ...session, program }} withText />}
          </span>
        </div>
      </Box>
    </Collapse>
  );
};

const ProgramPresentation: React.FC<{
  program: AbstractProgram;
  theme: Theme;
  sessionId: number;
}> = (props) => {
  const { t, ready } = useTranslation(["program", "general", "replay"]);
  const { program, theme, sessionId } = props;
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const [numberOfDisplayedReplays, setNumberOfDisplayedReplays] = useState(3);
  const classes = useStyles(props);

  const handleDisplaymore = () => {
    setNumberOfDisplayedReplays(numberOfDisplayedReplays + 3);
  };

  const filteredReplays = useMemo(() => {
    if (program?.sessions) {
      const replays = program.sessions
        .filter((session) => session.mp4Url || session.hlsUrl);
      
      const sortedReplays = replays
        .filter((replay) => replay.id !== +sessionId)
        .sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());
      
      return sortedReplays;
    }
  }, [program, sessionId]);
  

  if (!ready) {
    return null;
  }

  return (
    <Grid
      container
      direction={isMobile ? "column" : "row"}
      justify="space-between"
      style={{ marginTop: 32 }}
    >
      <Grid
        container
        spacing={2}
        style={{ width: isMobile ? "100%" : "50%", alignContent: "start" }}
      >
        <Grid item xs={12}>
          <Text variant="h4" gutterBottom>
            {t("program:aboutClass")}
          </Text>
        </Grid>

        <Grid item xs={12}>
          <RichText text={program?.description} noPadding />
        </Grid>

        {program?.goodToKnow?.length > 0 && (
          <Grid item xs={12}>
            <Text variant="h4" gutterBottom>
              {t("program:goodToKnow")}
            </Text>

            <Text component="ul">
              {program.goodToKnow.map((goodToKnow, i) => {
                return <li key={i}>{goodToKnow}</li>;
              })}
            </Text>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box mt={4} mb={5}>
            <Text variant="h4" gutterBottom>
              {t("program:latestClasses.title")}
            </Text>
          </Box>
        </Grid>
        {filteredReplays?.length > 0 ? (
          <>
            {filteredReplays
              .map((replay, index) => (
                <Fragment key={index}>
                  {index >= numberOfDisplayedReplays ? null : (
                    <>
                      {index > 0 && (
                        <Grid item xs={12}>
                          <Divider />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <ReplayListItem
                          size={"default"}
                          replay={replay}
                          program={program}
                          link={
                            {
                              pathname: `/replay/${program.id}/${replay.id}`,
                            } as any
                          }
                          likeable={<FavouriteSession session={replay} />}
                        />
                      </Grid>
                      {index === numberOfDisplayedReplays - 1 &&
                        index < filteredReplays.length - 1 && (
                          <Grid
                            item
                            onClick={handleDisplaymore}
                            style={{
                              width: "100%",
                              textAlign: "center",
                            }}
                          >
                            <Divider />
                            <Button
                              variant="contained"
                              onClick={handleDisplaymore}
                              className={classes.displayMoreButton}
                            >
                              {t("general:buttons.displayMore")}
                            </Button>
                          </Grid>
                        )}
                    </>
                  )}
                </Fragment>
              ))}
          </>
        ) : (
          <Grid item xs={12}>
            {t("replay:noProgramReplays")}
          </Grid>
        )}
      </Grid>
      {program?.instructor?.instructorProfile && (
        <Grid
          item
          style={{
            width: isMobile ? "100%" : "40%",
            marginTop: isMobile ? theme.spacing(4) : 0,
          }}
        >
          <InstructorCard instructor={program?.instructor} />
        </Grid>
      )}
    </Grid>
  );
};

export default (props) => {
  const classes = useStyles();
  const { sessionId, programId } = useParams() as any;
  const { t, ready } = useTranslation("seo");
  const theme = useTheme();
  const { user } = useContext(KvixUserContext);
  const program = useFetchProgram(+programId);
  const isMobile = useIsMobile();
  const accessLevel = useUserAccessLevel(user);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [sessionId]);

  const session = useMemo(() => {
    if (!program) {
      return null;
    }

    return program.sessions.find((session) => session.id === +sessionId);
  }, [program, sessionId]);

  if (session && !session.hlsUrl && !session.mp4Url) {
    return <Redirect to="/page-not-found" />;
  }

  if (!ready) {
    return null;
  }

  return (
    <Page
      meta={
        session && program
          ? {
              title: t("seo:anytime.title", {
                title: `Anytime - ${program.title}`,
              }),
              description: program.description,
              ogTitle: program.title,
              ogImage: program.posterUrl || program.imageUrl,
            }
          : {}
      }
    >
      <Container maxWidth={false} className={classes.containerPaddingBottom}>
        <>
          {program && session ? (
            <>
              {(
                session?.requiredProductAnytime
                  ? accessLevel === AccessLevel.FULL
                  : accessLevel !== AccessLevel.NONE
              ) ? (
                <ReplayContent
                  replay={session}
                  imageUrl={program.imageUrl}
                  name={program.instructor.name}
                  title={program.title}
                />
              ) : (
                <PremiumVideoPlaceholder session={session} user={user} />
              )}
            </>
          ) : (
            <Placeholder.Replay />
          )}
        </>
        <Box
          maxWidth={"lg"}
          marginX={isMobile ? "" : "80px"}
          marginBottom={props.comments?.length > 0 ? "32px" : "0px"}
        >
          <ReplayPresentation
            program={program}
            session={session}
            theme={theme}
          />
          <ProgramPresentation
            program={program}
            theme={theme}
            sessionId={sessionId}
          />
        </Box>
        <ProgramComments comments={props.comments} />
      </Container>
      <Footer noGutter />
    </Page>
  );
};
